import classNames from "classnames";
import React from "react";
import Measure, { ContentRect } from "react-measure";
import { Motion, spring } from "react-motion";
import _ from "underscore";
import { springConfig } from "../../../shared/helpers/motionHelper";

import "./currencySelector.scss";

interface OwnProps {
  selectedCurrency: ICurrency;
  currencies: ICurrency[];
  onCurrencySelect: (currency: ICurrency) => void;
  canChangeCurrency: boolean;
}

interface State {
  open: boolean;
  sliderXPos: number;
  sliderWidth: number;
  innerDimensions: ContentRect;
  optionDimensions: ContentRect;
}

export class CurrencySelector extends React.Component<OwnProps, State> {
  constructor(props: OwnProps) {
    super(props);

    this.state = {
      open: false,
      sliderXPos: 0,
      sliderWidth: 0,
      innerDimensions: null,
      optionDimensions: null,
    };
  }

  private onClick = (): void => {
    this.setState({ open: !this.state.open });
  }

  private onCurrencySelect = (currency: ICurrency): void => {
    this.setState({ open: false, sliderXPos: 0 });
    this.props.onCurrencySelect(currency);
  }

  private slideRight = (): void => {
    const limit: number = this.state.sliderWidth - this.state.innerDimensions.bounds.width;
    const x: number = Math.max(-limit, this.state.sliderXPos - 50);
    this.setState({ sliderXPos: x });
  }

  private slideLeft = (): void => {
    const x: number = Math.min(0, this.state.sliderXPos + 50);
    this.setState({ sliderXPos: x });
  }

  private setSliderWidth = (dimensions: ContentRect): number => {
    const optionDimensions = this.state.optionDimensions;
    const optionWidthTotal = optionDimensions.bounds.width + optionDimensions.margin.left + optionDimensions.margin.right;
    const optionHeightTotal = optionDimensions.bounds.height + optionDimensions.margin.top + optionDimensions.margin.bottom;

    const rows: number = Math.floor(dimensions.bounds.height / optionHeightTotal);
    const sliderWidth: number = optionWidthTotal * this.props.currencies.length / rows;

    return sliderWidth;
  }

  private renderCurrencyOptions(cssStyle) {
    const { selectedCurrency, currencies } = this.props;

    // Show the selected currency is at the beginning of the currency options list.
    const availableCurrencies = selectedCurrency != null
      ? [selectedCurrency, ...currencies.filter(c => c.code !== selectedCurrency.code)]
      : currencies;

    return (
      <div className="currency-selection-options" style={cssStyle}>
        {availableCurrencies.map((currency, index) => (
          <Measure
            key={currency.code}
            bounds
            margin
            onResize={(dimensions: ContentRect) => {
              if (index === (currencies.length - 1)) {
                this.setState({ optionDimensions: dimensions });
              }
            }}>
            {({ measureRef }) => {
              const isSelected = selectedCurrency && selectedCurrency.code === currency.code;
              return (
                <div
                  ref={measureRef}
                  onClick={() => this.onCurrencySelect(currency)}
                  className={classNames("currency-selection-option", { "is-selected": isSelected })}
                >
                  <img src={`/images/flags/currency/${currency.code}.png`} />
                  <div className="option-code">{currency.code}</div>
                </div>
              );
            }}
          </Measure>
        ))}
      </div>
    );
  }

  public render() {
    const { selectedCurrency, canChangeCurrency } = this.props;
    const display: string = this.state.open ? "block" : "none";

    return (
      <div className="currency-selector">
        {selectedCurrency && (
          <button disabled={!canChangeCurrency} onClick={this.onClick}>
            <img src={`/images/flags/currency/${selectedCurrency.code}.png`} />
            <div className="currency-selector-code">{selectedCurrency.code}</div>
          </button>
        )}

        <div className="currency-selection" style={{ display }}>
          <button onClick={this.slideLeft} className="currency-selector-control currency-selector-left-control">
            <i className="icon icon-arrow-left" />
          </button>
          <button onClick={this.slideRight} className="currency-selector-control currency-selector-right-control">
            <i className="icon icon-arrow-right" />
          </button>

          <Motion style={{ x: spring(this.state.sliderXPos, springConfig) }} >
            {({ x }) => {

              const cssStyle: React.CSSProperties = {
                width: this.state.sliderWidth ? this.state.sliderWidth : "auto",
                transform: `translateX(${x}px)`,
              };

              return (
                <Measure
                  bounds
                  onResize={(dimensions: ContentRect) => {
                    this.setState({ innerDimensions: dimensions, sliderWidth: this.setSliderWidth(dimensions) });
                  }}>
                  {({ measureRef }) => (
                    <div ref={measureRef} className="currency-selection-inner">
                      {this.renderCurrencyOptions(cssStyle)}
                    </div>
                  )}
                </Measure>
              );
            }}
          </Motion>
        </div>
      </div>
    );
  }
}
